/** 채팅 iframe 기본 폰트 스택 (미지정/실패 시 유지) */
export const DEFAULT_FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, sans-serif';

const STYLE_ID = 'buzzni-custom-font-face';
const LINK_ID = 'buzzni-custom-font-link';

type CustomFontOptions = {
  fontFamily?: string | null;
  fontUrl?: string | null;
};

const setFontFamilyVar = (family: string) => {
  document.documentElement.style.setProperty('--font-family', family);
  document.body.style.setProperty('--font-family', family);
};

const clearFontFamilyVar = () => {
  document.documentElement.style.removeProperty('--font-family');
  document.body.style.removeProperty('--font-family');
};

/** 커스텀 폰트 관련 DOM/CSS 변수를 제거하고 기본 폰트로 되돌린다. */
export const resetCustomFont = () => {
  clearFontFamilyVar();
  document.getElementById(STYLE_ID)?.remove();
  document.getElementById(LINK_ID)?.remove();
};

/**
 * 스크립트/postMessage 로 전달된 커스텀 폰트를 적용한다.
 * - 값이 없거나 로드 실패 시 기본 폰트 유지
 * - fontUrl 이 css/google fonts 면 link 로 로드
 * - fontUrl 이 폰트 파일이면 @font-face + FontFace API 로 로드 (fontFamily 필수)
 */
export const applyCustomFont = (options?: CustomFontOptions) => {
  if (typeof document === 'undefined') {
    return;
  }

  const fontFamily = (options?.fontFamily ?? '').trim();
  const fontUrl = (options?.fontUrl ?? '').trim();

  resetCustomFont();

  if (!fontFamily && !fontUrl) {
    return;
  }

  const handleLoadError = () => {
    resetCustomFont();
  };

  if (fontUrl) {
    const isStylesheet =
      /\.css(\?|$)/i.test(fontUrl) ||
      /fonts\.googleapis\.com/i.test(fontUrl) ||
      /fonts\.gstatic\.com/i.test(fontUrl);

    if (isStylesheet) {
      const link = document.createElement('link');
      link.id = LINK_ID;
      link.rel = 'stylesheet';
      link.href = fontUrl;
      link.onerror = handleLoadError;
      document.head.appendChild(link);
      if (fontFamily) {
        setFontFamilyVar(fontFamily);
      }
      return;
    }

    // raw font file — family 이름 없으면 적용 불가 → 기본 유지
    if (!fontFamily) {
      return;
    }

    const familyName = fontFamily.split(',')[0].trim().replace(/['"]/g, '');
    if (!familyName) {
      return;
    }

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
@font-face {
  font-family: '${familyName}';
  src: url('${fontUrl}');
  font-display: swap;
}
`;
    document.head.appendChild(style);
    setFontFamilyVar(fontFamily);

    if (typeof FontFace !== 'undefined' && document.fonts) {
      try {
        const escapedUrl = fontUrl.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        const face = new FontFace(familyName, `url("${escapedUrl}")`);
        face
          .load()
          .then((loaded) => {
            document.fonts.add(loaded);
          })
          .catch(handleLoadError);
      } catch {
        // @font-face 선언만으로도 동작할 수 있으므로 유지
      }
    }
    return;
  }

  // family only (시스템 폰트 / 이미 로드된 웹폰트)
  if (fontFamily) {
    setFontFamilyVar(fontFamily);
  }
};
